import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { mergeClasses, Popover, PopoverSurface } from '@fluentui/react-components';
import shallow from 'zustand/shallow';
import { useTranslation } from 'react-i18next';
import Big, { bigZero } from '../../../math/math';
import { useGridStore, useEntriesMapByTaskAndWorkType, useTotalHoursByTaskAndWorkType, useIsGridReadOnly } from '../state';
import type { EntryWithBigValue } from '../state';
import { useInitialData } from '../../../hooks/useInitialData';
import { distributeTotal } from '../utils/distributeTotal';
import { useRegisterTimeHours } from '../hooks/useRegisterTimeHours';
import { useTimeGridStyles } from '../useTimeGridStyles';
import { extractValidationError, translateValidationError } from '../../../api/validationError';
import { resolveWorkTypeForApi } from '../utils/resolveWorkTypeForApi';

type TotalInputProps = {
    taskId: string;
    dates: string[];
    workType: string;
};

export const TotalInput = memo(function TotalInput({ taskId, dates, workType }: TotalInputProps) {
    const { t } = useTranslation('timesheet');
    const readOnly = useIsGridReadOnly();

    const {
        data: {
            configuration: { timeEntryRoundingInterval, workWeek, timeSheetValidation },
        },
    } = useInitialData();

    const { batchUpsertEntries, periodDataId } = useGridStore(
        state => ({
            batchUpsertEntries: state.batchUpsertEntries,
            periodDataId: state.periodData?.id ?? '',
        }),
        shallow,
    );

    const totalHours = useTotalHoursByTaskAndWorkType(taskId, workType);
    const entriesMap = useEntriesMapByTaskAndWorkType(taskId, workType);

    const [localValue, setLocalValue] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [warningOpen, setWarningOpen] = useState(false);
    const [warningValue, setWarningValue] = useState('');
    const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { mutate: registerTimeHours } = useRegisterTimeHours();

    const styles = useTimeGridStyles();

    const storeDisplayValue = useMemo(() => {
        // const fixed = totalHours.toFixed(2);
        const fixed = totalHours.toString();
        return fixed === '0' ? '' : fixed;
    }, [totalHours]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
    }, []);

    const handleFocus = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            const input = e.target;
            setIsEditing(true);

            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
                warningTimeoutRef.current = null;
            }
            setWarningOpen(false);

            // const num = Number(totalHours.toFixed(2));
            const num = totalHours.toNumber();
            setLocalValue(num === 0 ? '' : String(num));

            requestAnimationFrame(() => {
                input.select();
            });
        },
        [totalHours],
    );

    const showWarning = useCallback((message: string) => {
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
        setWarningValue(message);
        setWarningOpen(true);

        warningTimeoutRef.current = setTimeout(() => {
            setWarningOpen(false);
            warningTimeoutRef.current = null;
        }, 4000);
    }, []);

    const handleServerError = useCallback(
        async (error: unknown, previousEntries: EntryWithBigValue[]) => {
            // Revert store to previous values
            if (previousEntries.length > 0) {
                batchUpsertEntries(previousEntries);
            }

            // The HttpClient throws the Response clone on non-2xx.
            // Since registerTimeHours has no format (void response), the body isn't parsed.
            // We need to parse it ourselves to extract the validation error.
            // if (error && typeof error === 'object' && 'json' in error && typeof (error as Response).json === 'function') {
            if (error instanceof Response) {
                try {
                    const body = await error.json();
                    const validationError = extractValidationError(body);
                    if (validationError) {
                        showWarning(translateValidationError(t, validationError));
                        return;
                    }
                } catch {
                    // JSON parsing failed, fall through to generic handling
                }
            }

            // Try the standard extraction (for other error shapes)
            const validationError = extractValidationError(error);
            if (validationError) {
                showWarning(translateValidationError(t, validationError));
                return;
            }

            // Fallback to generic error message
            showWarning(error instanceof Error ? error.message : String(error));
        },
        [batchUpsertEntries, showWarning, t],
    );

    const handleBlur = useCallback(() => {
        setIsEditing(false);

        const numericValue = Number(localValue);
        const resolvedWorkWeek = workWeek ?? [];

        if (localValue === '' || Number.isNaN(numericValue) || numericValue <= 0) {
            // Skip if already zero — no change needed
            if (totalHours.eq(bigZero)) return;

            // Zero out workday entries only
            const targetDates = dates;

            // Capture previous entries for rollback
            const previousEntries = targetDates.map(date => entriesMap[date]).filter(Boolean);

            const entriesToUpdate: EntryWithBigValue[] = targetDates.map(date => {
                const existing = entriesMap[date];
                return {
                    userPeriodId: periodDataId,
                    taskId,
                    date,
                    value: bigZero,
                    workTypeId: workType,
                    flagged: existing?.flagged ?? false,
                    comment: existing?.comment ?? '',
                };
            });

            if (entriesToUpdate.length > 0) {
                batchUpsertEntries(entriesToUpdate);
                registerTimeHours(
                    entriesToUpdate.map(e => ({
                        ...e,
                        value: 0,
                        workTypeId: resolveWorkTypeForApi(workType),
                    })),
                    {
                        onError: (error: unknown) => {
                            void handleServerError(error, previousEntries);
                        },
                    },
                );
            }
            return;
        }

        const totalBig = new Big(numericValue.toString());

        // Skip if the total hasn't changed
        if (totalBig.eq(totalHours)) return;
        const result = distributeTotal(totalBig, dates, resolvedWorkWeek, timeEntryRoundingInterval ?? 0);

        if (result.ok) {
            // Validate against max hours per period if enabled (non-reactive read to avoid rerenders)
            if (timeSheetValidation?.maxHoursPerPeriodEnabled && timeSheetValidation.maxHoursAllowedPerPeriod != null) {
                const currentEntriesMap = useGridStore.getState().entriesMap;
                const grandTotal = Array.from(currentEntriesMap.values()).reduce((sum, entry) => sum.plus(entry.value), bigZero);
                const projectedGrandTotal = grandTotal.minus(totalHours).plus(totalBig);
                if (projectedGrandTotal.gt(new Big(timeSheetValidation.maxHoursAllowedPerPeriod.toString()))) {
                    showWarning(t('cell.error.periodMaxHours', { value: timeSheetValidation.maxHoursAllowedPerPeriod }));
                    return;
                }
            }

            // Capture previous entries for rollback
            const distributionDates = Object.keys(result.distribution);
            const previousEntries = distributionDates.map(date => entriesMap[date]).filter(Boolean);

            const entriesToUpdate: EntryWithBigValue[] = Object.entries(result.distribution).map(([date, value]) => {
                const existing = entriesMap[date];
                return {
                    userPeriodId: periodDataId,
                    taskId,
                    date,
                    value,
                    workTypeId: workType,
                    flagged: existing?.flagged ?? false,
                    comment: existing?.comment ?? '',
                };
            });

            batchUpsertEntries(entriesToUpdate);
            registerTimeHours(
                entriesToUpdate.map(entry => ({
                    ...entry,
                    value: Number(entry.value.toString()),
                    workTypeId: resolveWorkTypeForApi(workType),
                })),
                {
                    onError: (error: unknown) => {
                        void handleServerError(error, previousEntries);
                    },
                },
            );
        } else {
            showWarning(t('cell.error.distributeTotal', { value: numericValue.toString() }));
        }
    }, [
        localValue,
        workWeek,
        dates,
        entriesMap,
        periodDataId,
        taskId,
        workType,
        batchUpsertEntries,
        registerTimeHours,
        timeEntryRoundingInterval,
        t,
        showWarning,
        handleServerError,
        timeSheetValidation,
        totalHours,
    ]);

    const cellRef = useRef<HTMLDivElement>(null);

    return (
        <div ref={cellRef} className={mergeClasses(styles.cell, styles.cellTotalEditable)} role="gridcell">
            <input
                className={mergeClasses(styles.hoursInput, styles.totalInput, readOnly && styles.hoursInputReadOnly)}
                value={isEditing ? localValue : storeDisplayValue}
                type="text"
                aria-label={`Total hours for task ${taskId}`}
                readOnly={readOnly}
                tabIndex={readOnly ? -1 : undefined}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            <Popover open={warningOpen} positioning={{ target: cellRef.current, position: 'above', align: 'center' }} trapFocus={false} withArrow>
                <PopoverSurface>{warningValue}</PopoverSurface>
            </Popover>
        </div>
    );
});
